'use client';

import * as React from 'react';
import { useTranslations, useLocale } from 'next-intl';
import { CheckCircle2 } from 'lucide-react';
import { SectionWrapper } from '@/components/ui/section-wrapper';
import { Typography } from '@/components/ui/typography';
import { HeroBanner } from '@/components/frontend/common/hero-banner';
import { useScrollAnimation } from '@/hooks/use-scroll-animation';
import { cn } from '@/lib/utils';

type ChangelogEntry = {
    id: string;
    version: string;
    title?: string;
    description: string;
    features: string[];
    order: number;
};

export function ChangelogPageContent() {
    const t = useTranslations('changelogPage');
    const locale = useLocale();
    const [entries, setEntries] = React.useState<ChangelogEntry[]>([]);
    const [isLoading, setIsLoading] = React.useState(true);
    const sectionRef = useScrollAnimation({ threshold: 0.1 });

    React.useEffect(() => {
        const load = async () => {
            try {
                const res = await fetch(`/api/changelog?locale=${encodeURIComponent(locale)}`, {
                    cache: 'no-store',
                });
                if (!res.ok) {
                    setEntries([]);
                    return;
                }
                const data = await res.json();
                if (Array.isArray(data)) {
                    setEntries(data);
                } else {
                    setEntries([]);
                }
            } catch (error) {
                setEntries([]);
            } finally {
                setIsLoading(false);
            }
        };

        load();
    }, [locale]);

    return (
        <div className="min-h-screen bg-white dark:bg-black">
            <HeroBanner
                page="changelog"
                title={t('title')}
                description={t('description')}
                pill={t('pill')}
            />

            <SectionWrapper
                variant="default"
                padding="md"
                className="relative overflow-hidden "
            >
                {/* Decorative background */}
                <div className="pointer-events-none absolute inset-0 opacity-40">
                    <div className="absolute left-10 top-10 h-80 w-80 rounded-full bg-primary-500/10 blur-3xl" />
                    <div className="absolute right-10 bottom-10 h-80 w-80 rounded-full bg-blue-500/10 blur-3xl" />
                </div>

                <div className="container relative z-10 pt-2">
                    {isLoading && (
                        <div className="text-center text-gray-500 dark:text-gray-400 py-12">
                            {t('loading')}
                        </div>
                    )}

                    {!isLoading && entries.length === 0 && (
                        <div className="text-center text-gray-500 dark:text-gray-400 py-12">
                            {t('noEntriesAvailable')}
                        </div>
                    )}

                    <div className="space-y-8">
                        {entries.map((entry, index) => (
                            <div
                                key={entry.id}
                                ref={index === 0 ? sectionRef.ref : undefined}
                                className={cn(
                                    "relative transition-all duration-700 opacity-100 translate-y-0"
                                )}
                                style={{ transitionDelay: `${Math.min(index * 100, 500)}ms` }}
                            >
                                {/* Version Card */}
                                <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm hover:shadow-md transition-all duration-300 overflow-hidden">
                                    {/* Version Header */}
                                    <div className="px-8 py-6 border-b border-gray-200 dark:border-gray-800">
                                        <div>
                                            <Typography variant="h2" className="text-gray-900 dark:text-white text-2xl md:text-3xl font-bold">
                                                {entry.title || `${t('versionLabel')} ${entry.version}`}
                                            </Typography>

                                        </div>
                                    </div>

                                    {/* Version Content */}
                                    <div className="p-8">
                                        {/* Description */}
                                        {entry.description && (
                                            <div className="mb-8">
                                                <Typography variant="p" className="text-gray-700 dark:text-gray-300 leading-relaxed text-lg">
                                                    {entry.description}
                                                </Typography>
                                            </div>
                                        )}

                                        {/* Features List */}
                                        {entry.features && entry.features.length > 0 && (
                                            <div>
                                                <Typography variant="h3" className="text-gray-900 dark:text-white text-xl font-semibold mb-4">
                                                    {t('versionFeatures')}
                                                </Typography>
                                                <ul className="space-y-3">
                                                    {entry.features.map((feature, featureIndex) => (
                                                        <li
                                                            key={featureIndex}
                                                            className="flex items-start gap-3 text-gray-700 dark:text-gray-300"
                                                        >
                                                            <div className="flex-shrink-0 w-5 h-5 rounded-full bg-primary-500 dark:bg-primary-600 flex items-center justify-center mt-0.5">
                                                                <CheckCircle2 className="w-3.5 h-3.5 text-white" />
                                                            </div>
                                                            <Typography variant="p" className="leading-relaxed">
                                                                {feature}
                                                            </Typography>
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </SectionWrapper>
        </div>
    );
}

